import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useNurseTimeOff } from "../../hooks/useNurseTimeOff";
import { useShiftTimes } from "../../hooks/useShiftTimes";
import type { CostPredictionResponse, Nurse, NurseData, WeekData } from "../../utils/models";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";
import { DIFF_HOURS_WARNING } from "../../utils/warningDialogs/utils";
import { buildCandidateRow, type CandidateRowContext } from "./addStaffRows";

const DAYS_IN_STRIP = 7;

type Args = {
	candidates: readonly Nurse[];
	/** `null` while no shift is picked — there are no rows to build until then. */
	window: ShiftAvailabilityWindow | null;
	/** The panel's schedule weeks; `weekIndex` and `dayNo` are positions in them. */
	weeks: WeekData[];
	weekIndex: number;
	dayNo: number;
	shiftNo: number;
	staffingDate: DateTime;
	predictions: Record<number, CostPredictionResponse>;
	nurseOccurrences: Record<number, number>;
	showActual: boolean;
};

/** The seven days the row's week strip shows. */
const useWeekStrip = (staffingDate: DateTime): DateTime[] =>
	useMemo<DateTime[]>(() => {
		const startOfWeek = staffingDate.startOf("week");
		return Array.from({ length: DAYS_IN_STRIP }, (_, i) => startOfWeek.plus({ day: i }));
	}, [staffingDate]);

/**
 * The step-2 grid rows: every candidate with their week, predicted cost, and whether they can work the
 * picked window (`resolveShiftAvailability` — blocked rows carry the reason, available rows the soft
 * badges). Rebuilds when the window, the candidates or the predictions change.
 */
export const useAddStaffRows = ({ candidates, window, weeks, weekIndex, dayNo, shiftNo, staffingDate, predictions, nurseOccurrences, showActual }: Args) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { getShiftTimes } = useShiftTimes();
	const lastNameFirst = useLastNameFirst();
	const timeOffOf = useNurseTimeOff();
	const dateRange = useWeekStrip(staffingDate);

	const rows = useMemo<NurseData[]>(() => {
		if (!window) return [];
		const context: CandidateRowContext = {
			window,
			weeks,
			weekIndex,
			dayNo,
			shiftNo,
			staffingDate,
			dateRange,
			weeklyShifts: weeks[weekIndex].weeklyShifts,
			departmentId: chosenDepartment.department_id,
			dayStart: chosenDepartment.dayStart,
			timeOffOf,
			// DEFAULT: a department with no break rules deducts no breaks — same as the old picker's `|| []`.
			breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
			// DEFAULT: the same long-day limit the daily-hours warning dialog falls back to (useNurseWarnings).
			longShiftDurationHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
			predictions,
			nurseOccurrences,
			showActual,
			getShiftTimes,
			t,
			lastNameFirst,
		};
		return candidates.map((nurse) => buildCandidateRow(context, nurse));
	}, [
		window,
		candidates,
		chosenDepartment,
		weeks,
		weekIndex,
		dayNo,
		shiftNo,
		staffingDate,
		dateRange,
		timeOffOf,
		predictions,
		nurseOccurrences,
		showActual,
		getShiftTimes,
		t,
		lastNameFirst,
	]);

	const availableCount = useMemo(() => rows.filter((row) => row.unavailableReason == null).length, [rows]);

	return { rows, dateRange, availableCount };
};
